import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import PersonIcon from "@material-ui/icons/Person";
import { api } from "../../../Pages/Common/Provision/Provision.helper";
import useStyles from "../../../Pages/Common/Provision/Provision.styles";

/**
 * Contact-details preview for a professional's name (Story 56425, DFXX13-15, DFXX18-19).
 *
 * **OQ2 is resolved as the hover popover**, not two extra inline fields per professional: hovering
 * a professional's name ANYWHERE on the Provision card previews the email address and contact
 * number held for them in the central repository. Every place the card shows a professional goes
 * through this one component — the three role pickers, the 4th-party company picker and the
 * 4th-party contact read-out — so the presentation is decided here rather than per caller.
 *
 * Built on MUI's Tooltip rather than a hand-rolled absolutely-positioned panel: it portals out of
 * the card (the old version was clipped by the card's own bounds), places and flips itself, closes
 * on Escape, and opens on focus as well as hover so a keyboard user can reach it too.
 * `interactive` is left at its default (false) so the panel can never steal the hover that opened
 * it, which is what made the previous popover flicker.
 *
 * Two sources for the details, deliberately:
 *   - `professionalId` — fetched from `GET /professionals/{id}` on first hover, then cached.
 *   - `detail`         — for a contact the caller ALREADY holds in full (the 4th party's contacts
 *                        come back from `listCompanyContacts` complete with email and number), so
 *                        hovering one of those costs no request at all.
 */

/**
 * `professionalId` -> ProfessionalDetail, shared by every instance for the life of the page. The
 * same professional can appear in more than one place on the card and a name can be hovered any
 * number of times; neither should re-hit the API. Module scope (not a ref) so it survives
 * remounts — which is exactly the case it exists for, since the sections remount on Cancel/Save.
 */
const detailCache = new Map();

/**
 * Fetch (or return the cached) full details for a professional. Exported so callers that already
 * need a professional's name on load — ProfessionalsSection resolves the draft's ids into display
 * names — go through the same cache, and hovering one of those names afterwards costs nothing.
 * Without this the card would fetch every professional twice: once to label the field, once to
 * fill the preview.
 * @param {string} professionalId
 * @returns {Promise<?import("../../../../../domain/provision/provision.types").ProfessionalDetail>}
 */
export function loadProfessionalDetail(professionalId) {
  if (!professionalId) return Promise.resolve(null);
  const cached = detailCache.get(professionalId);
  if (cached) return Promise.resolve(cached);
  return api.getProfessional(professionalId).then((detail) => {
    if (detail) detailCache.set(professionalId, detail);
    return detail;
  });
}

/** Test seam only — the cache is module-level and would otherwise leak between test cases. */
export function clearProfessionalDetailCache() {
  detailCache.clear();
}

const ProfessionalHoverPopover = ({
  professionalId,
  name,
  companyName,
  detail,
  children,
  disabled,
  placement,
}) => {
  const classes = useStyles();
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const resolved =
    detail || fetched || (professionalId ? detailCache.get(professionalId) : null) || null;

  /**
   * Fetch on first open, never on mount: a card with four professionals on it must not fire four
   * requests for panels nobody has looked at (the same reasoning as M_ProfessionalPicker's
   * open-driven search). The cache makes every open after the first free.
   */
  const load = useCallback(() => {
    if (detail || !professionalId || loading) return;
    const cached = detailCache.get(professionalId);
    if (cached) {
      setFetched(cached);
      return;
    }
    setLoading(true);
    setFailed(false);
    loadProfessionalDetail(professionalId)
      .then(setFetched)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [detail, professionalId, loading]);

  // Whether there is anything to preview at all. Callers never have to branch on "do I hold an id
  // for this one?" themselves — they pass what they have and this stands down on its own.
  const live = !disabled && Boolean(professionalId || detail);

  // Hover-only, no tab stop, when the caller supplies its own trigger: `children` is a whole form
  // field whose input is already the focusable thing in that spot, and adding a second tab stop
  // around it would put a stop in front of every picker on the card. A bare name IS focusable —
  // that is the only way a keyboard user reaches the preview.
  const anchor = children ? (
    <div className={classes.previewAnchor}>{children}</div>
  ) : (
    <span className={live ? classes.previewName : undefined} tabIndex={live ? 0 : undefined}>
      {name || "-"}
    </span>
  );

  const title = (
    <div className={classes.previewBody}>
      <div className={classes.previewHeading}>
        {resolved?.name || name || "Contact details"}
        {companyName || resolved?.companyName ? (
          <span className={classes.previewCompany}>{companyName || resolved.companyName}</span>
        ) : null}
      </div>
      {loading && !resolved && (
        <div className={classes.previewRow}>
          <CircularProgress size={11} color="inherit" /> Loading contact details&hellip;
        </div>
      )}
      {failed && !resolved && (
        <div className={classes.previewRow}>Couldn&rsquo;t load contact details.</div>
      )}
      {resolved && (
        <>
          {/* The name of the repository contact the email/number belong to, when the API tells us
              (a firm's details are its main contact's). Absent for a contact hovered directly —
              its own name is already the heading above. */}
          {resolved.contactName && resolved.contactName !== resolved.name && (
            <div className={classes.previewRow}>
              <PersonIcon fontSize="inherit" /> {resolved.contactName}
            </div>
          )}
          <div className={classes.previewRow}>
            <EmailIcon fontSize="inherit" /> {resolved.email || "No email address held"}
          </div>
          <div className={classes.previewRow}>
            <PhoneIcon fontSize="inherit" /> {resolved.contactNumber || "No contact number held"}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Tooltip
      /* MUI keeps a Tooltip mounted but never opens it when the title is "", which is how this
         stands down (nothing selected yet, or the picker's own dropdown is open) WITHOUT changing
         the element tree. Swapping between a wrapped and an unwrapped child instead would remount
         the field it wraps every time that dropdown opened, throwing away the user's focus and
         typing mid-interaction. */
      title={live ? title : ""}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={120}
      classes={{ tooltip: classes.previewTooltip, arrow: classes.previewArrow }}
      onOpen={load}
    >
      {anchor}
    </Tooltip>
  );
};

ProfessionalHoverPopover.defaultProps = {
  disabled: false,
  placement: "top-start",
};

ProfessionalHoverPopover.propTypes = {
  /** Repository id — the details are fetched (once, then cached) on first hover. */
  professionalId: PropTypes.string,
  /** Name to show as the trigger when no `children` are given, and as the panel's heading. */
  name: PropTypes.string,
  companyName: PropTypes.string,
  /**
   * Already-known details ({name, email, contactNumber}) — pass these instead of an id when the
   * caller holds them, and no request is made at all.
   */
  detail: PropTypes.object,
  /** Custom trigger (a whole form field, say). Hover-only; a bare name is focusable instead. */
  children: PropTypes.node,
  /** Render the trigger with no popover at all, e.g. while nothing is selected. */
  disabled: PropTypes.bool,
  placement: PropTypes.string,
};

export default ProfessionalHoverPopover;
